import { Preferences } from "@capacitor/preferences";
import { isNativePreferencesBackend } from "./platform";
import type { AuthSessionResponse, DeviceLogin, StoredUserProfile } from "./authTypes";

const KEY_ACCESS = "access_token";
const KEY_REFRESH = "refresh_token";
const KEY_PROFILE = "user_profile";
// Survives a dead session on purpose (clear() keeps it): it is how an
// account without e-mail gets back in. Removed only by clearDeviceLogin()
// on logout, account deletion, or when the server refuses it.
const KEY_DEVICE_LOGIN = "device_login";

async function prefGet(key: string): Promise<string | null> {
  const { value } = await Preferences.get({ key });
  return value ?? null;
}

async function prefSet(key: string, value: string): Promise<void> {
  await Preferences.set({ key, value });
}

async function prefRemove(key: string): Promise<void> {
  await Preferences.remove({ key });
}

export const tokenStorage = {
  async getAccessToken(): Promise<string | null> {
    if (isNativePreferencesBackend()) {
      return prefGet(KEY_ACCESS);
    }
    try {
      return localStorage.getItem(KEY_ACCESS);
    } catch {
      return null;
    }
  },

  async getRefreshToken(): Promise<string | null> {
    if (isNativePreferencesBackend()) {
      return prefGet(KEY_REFRESH);
    }
    try {
      return localStorage.getItem(KEY_REFRESH);
    } catch {
      return null;
    }
  },

  async getUserProfile(): Promise<StoredUserProfile | null> {
    let raw: string | null;
    if (isNativePreferencesBackend()) {
      raw = await prefGet(KEY_PROFILE);
    } else {
      try {
        raw = localStorage.getItem(KEY_PROFILE);
      } catch {
        raw = null;
      }
    }
    if (!raw) return null;
    try {
      return JSON.parse(raw) as StoredUserProfile;
    } catch {
      return null;
    }
  },

  async setSession(session: AuthSessionResponse): Promise<void> {
    const profileJson = JSON.stringify(session.user);
    if (session.deviceSecret && session.user?.id) {
      await this.setDeviceLogin({ userId: session.user.id, deviceSecret: session.deviceSecret });
    }
    if (isNativePreferencesBackend()) {
      await prefSet(KEY_ACCESS, session.accessToken);
      await prefSet(KEY_REFRESH, session.refreshToken);
      await prefSet(KEY_PROFILE, profileJson);
      return;
    }
    try {
      localStorage.setItem(KEY_ACCESS, session.accessToken);
      localStorage.setItem(KEY_REFRESH, session.refreshToken);
      localStorage.setItem(KEY_PROFILE, profileJson);
    } catch (e) {
      console.warn("tokenStorage.setSession localStorage failed", e);
    }
  },

  async getDeviceLogin(): Promise<DeviceLogin | null> {
    let raw: string | null;
    if (isNativePreferencesBackend()) {
      raw = await prefGet(KEY_DEVICE_LOGIN);
    } else {
      try {
        raw = localStorage.getItem(KEY_DEVICE_LOGIN);
      } catch {
        raw = null;
      }
    }
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as DeviceLogin;
      return parsed?.userId && parsed?.deviceSecret ? parsed : null;
    } catch {
      return null;
    }
  },

  async clearDeviceLogin(): Promise<void> {
    if (isNativePreferencesBackend()) {
      await prefRemove(KEY_DEVICE_LOGIN);
      return;
    }
    try {
      localStorage.removeItem(KEY_DEVICE_LOGIN);
    } catch {
      /* ignore */
    }
  },

  async setDeviceLogin(login: DeviceLogin): Promise<void> {
    const json = JSON.stringify(login);
    if (isNativePreferencesBackend()) {
      await prefSet(KEY_DEVICE_LOGIN, json);
      return;
    }
    try {
      localStorage.setItem(KEY_DEVICE_LOGIN, json);
    } catch {
      /* ignore */
    }
  },

  async setUserProfileOnly(profile: StoredUserProfile): Promise<void> {
    const profileJson = JSON.stringify(profile);
    if (isNativePreferencesBackend()) {
      await prefSet(KEY_PROFILE, profileJson);
      return;
    }
    try {
      localStorage.setItem(KEY_PROFILE, profileJson);
    } catch {
      /* ignore */
    }
  },

  async clear(): Promise<void> {
    if (isNativePreferencesBackend()) {
      await prefRemove(KEY_ACCESS);
      await prefRemove(KEY_REFRESH);
      await prefRemove(KEY_PROFILE);
      return;
    }
    try {
      localStorage.removeItem(KEY_ACCESS);
      localStorage.removeItem(KEY_REFRESH);
      localStorage.removeItem(KEY_PROFILE);
    } catch {
      /* ignore */
    }
  },
};
